import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getCampaigns, getCampaignHistory } from "../api/client.js";
import { Shell } from "../components/Shell.jsx";
import { Icon } from "../components/Icon.jsx";
import { Panel, StatusPill, Loading, ErrorCard, formatCount } from "../components/ui.jsx";
import { DOMAIN_LABELS } from "../domainModel.js";
import { campaignBuilderRoute, campaignRoute, monitoringRoute } from "../navConfig.js";

const FILTERS = ["all", "draft", "ready_for_review", "approved", "deployed", "completed", "cancelled"];
const FILTER_LABELS = {
  all: "All",
  ready_for_review: "Ready for review",
  deployed: "Published",
};

const SORT_DEFAULTS = {
  name: "asc",
  program: "asc",
  runMonth: "desc",
  cohort: "desc",
  governance: "asc",
  workflow: "asc",
  updatedAt: "desc",
};

const GOVERNANCE_RANK = { fail: 0, warn: 1, pass: 2 };

function CampaignDate({ value }) {
  if (!value) return "Unavailable";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return (
    <time dateTime={value}>
      {new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(date)}
    </time>
  );
}

function campaignSortValue(campaign, key) {
  if (key === "program") return DOMAIN_LABELS[campaign.programDomain] || campaign.programDomain || "";
  if (key === "cohort") return campaign.cohortEstimate?.memberCount ?? -1;
  if (key === "governance") return GOVERNANCE_RANK[campaign.governance?.overall] ?? 3;
  if (key === "workflow") return campaign.workflowStatus || "";
  return campaign[key] || "";
}

function SortHeader({ column, label, sort, onSort, align }) {
  const active = sort.key === column;
  const direction = active ? sort.direction : null;
  const nextDirection = active && direction === "asc" ? "descending" : "ascending";
  const sortDescription = active
    ? `${label}, sorted ${direction === "asc" ? "ascending" : "descending"}. Sort ${nextDirection}`
    : `${label}, not sorted. Sort ascending`;
  return (
    <th
      scope="col"
      style={align ? { textAlign: align } : undefined}
      aria-sort={active ? (direction === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        className={`campaign-sort${active ? " active" : ""}`}
        type="button"
        aria-label={sortDescription}
        title={sortDescription}
        onClick={() => onSort(column)}
      >
        <span>{label}</span>
        <Icon
          name="chev-down"
          size={13}
          style={{ transform: direction === "asc" ? "rotate(180deg)" : undefined }}
        />
      </button>
    </th>
  );
}

function publicationStatus(value) {
  if (["ready", "publication_ready", "campaign_published"].includes(value)) {
    return { status: "approved", label: "Available for delivery" };
  }
  if (["blocked", "publication_blocked"].includes(value)) {
    return { status: "fail", label: "Publication blocked" };
  }
  return { status: "neutral", label: "Unavailable" };
}

function governanceStatus(value) {
  if (value === "pass") return { status: value, label: "Checks passed" };
  if (value === "warn") return { status: value, label: "Review warning" };
  if (value === "fail") return { status: value, label: "Blocked" };
  return { status: "neutral", label: "Not checked" };
}

export function ActivationWorkspace({ tenant, auth }) {
  const [campaigns, setCampaigns] = useState(null);
  const [history, setHistory] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "updatedAt", direction: "desc" });
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([getCampaigns(), getCampaignHistory()])
      .then(([campaignData, historyData]) => {
        setCampaigns(campaignData.campaigns);
        setHistory(historyData.records);
      })
      .catch(setError);
  }, []);

  const visibleCampaigns = useMemo(() => {
    if (!campaigns) return [];
    const query = search.trim().toLocaleLowerCase();
    const multiplier = sort.direction === "asc" ? 1 : -1;
    return campaigns
      .filter((campaign) => filter === "all" || campaign.workflowStatus === filter)
      .filter((campaign) => !query || campaign.name.toLocaleLowerCase().includes(query))
      .sort((left, right) => {
        const a = campaignSortValue(left, sort.key);
        const b = campaignSortValue(right, sort.key);
        if (typeof a === "number" && typeof b === "number") return (a - b) * multiplier;
        return String(a).localeCompare(String(b), undefined, { numeric: true }) * multiplier;
      });
  }, [campaigns, filter, search, sort]);

  const changeSort = (key) =>
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
        : { key, direction: SORT_DEFAULTS[key] }
    );

  const body = () => {
    if (error) return <ErrorCard error={error} />;
    if (!campaigns) return <Loading />;
    const campaignById = new Map(campaigns.map((campaign) => [campaign.id, campaign]));

    return (
      <>
        <div className="filter-scroll" role="group" aria-label="Campaign status filters">
          {FILTERS.map((f) => (
            <button
              key={f}
              className={"chip" + (f === filter ? " on" : "")}
              type="button"
              aria-pressed={f === filter}
              aria-controls="campaigns-table"
              onClick={() => setFilter(f)}
            >
              {FILTER_LABELS[f] || f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        <Panel
          full
          title="Campaigns"
          desc="Cohort-level campaigns organized by governance and workflow state"
        >
          <div className="campaign-table-tools">
            <label className="campaign-search">
              <span className="sr-only">Search campaigns by name</span>
              <Icon name="search" size={16} />
              <input
                type="search"
                value={search}
                placeholder="Search campaign names"
                autoComplete="off"
                aria-controls="campaigns-table"
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
            <span role="status" aria-live="polite" aria-atomic="true">
              {visibleCampaigns.length} of {campaigns.length} campaigns
            </span>
          </div>
          <div
            id="campaigns-table"
            className="table-scroll"
            tabIndex={0}
            role="region"
            aria-label="Campaigns table"
          >
            <table className="ae-table campaign-admin-table">
              <caption className="sr-only">
                Campaign records. Activate a sortable column heading to change the table order.
              </caption>
              <thead>
                <tr>
                  <SortHeader column="name" label="Campaign" sort={sort} onSort={changeSort} />
                  <SortHeader column="program" label="Program" sort={sort} onSort={changeSort} />
                  <SortHeader column="runMonth" label="Evidence period" sort={sort} onSort={changeSort} />
                  <SortHeader
                    column="cohort"
                    label="Audience (covered people)"
                    sort={sort}
                    onSort={changeSort}
                    align="right"
                  />
                  <SortHeader column="governance" label="Governance" sort={sort} onSort={changeSort} />
                  <SortHeader column="workflow" label="Workflow" sort={sort} onSort={changeSort} />
                  <SortHeader column="updatedAt" label="Updated" sort={sort} onSort={changeSort} />
                  <th scope="col">Action</th>
                </tr>
              </thead>
              <tbody>
                {visibleCampaigns.map((c) => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 600 }}>
                      {c.syntheticDemo && !c.sampleData ? (
                        <Link
                          className="ae-row-link"
                          to={monitoringRoute({
                            programDomain: c.programDomain,
                            runMonth: c.impactRunMonth,
                          })}
                        >
                          {c.name}
                        </Link>
                      ) : (
                        <Link className="ae-row-link" to={campaignRoute(c.id)}>
                          {c.name}
                        </Link>
                      )}
                    </td>
                    <td>{DOMAIN_LABELS[c.programDomain] || c.programDomain}</td>
                    <td>{c.runMonth}</td>
                    <td style={{ textAlign: "right", fontWeight: 700 }}>
                      {formatCount(c.cohortEstimate?.memberCount)}
                    </td>
                    <td>
                      <StatusPill {...governanceStatus(c.governance?.overall)} />
                    </td>
                    <td>
                      <StatusPill status={c.workflowStatus} />
                    </td>
                    <td style={{ color: "var(--bi-muted)", fontSize: 12 }}>
                      <CampaignDate value={c.updatedAt} />
                    </td>
                    <td>
                      <Link
                        className="campaign-row-action"
                        aria-label={`${
                          c.workflowStatus === "draft" ? "Continue campaign setup" : "View campaign"
                        }: ${c.name}`}
                        to={
                          c.syntheticDemo && !c.sampleData
                            ? monitoringRoute({
                                programDomain: c.programDomain,
                                runMonth: c.impactRunMonth,
                              })
                            : c.workflowStatus === "draft"
                              ? campaignBuilderRoute({
                                  source: "campaigns",
                                  campaignId: c.id,
                                  step: "program",
                                })
                              : campaignRoute(c.id)
                        }
                      >
                        {c.syntheticDemo && !c.sampleData
                          ? "View impact"
                          : c.workflowStatus === "draft"
                            ? "Continue campaign setup"
                            : "View campaign"}
                      </Link>
                    </td>
                  </tr>
                ))}
                {visibleCampaigns.length === 0 && (
                  <tr>
                    <td colSpan={8} role="status" style={{ color: "var(--bi-muted)" }}>
                      {search ? "No campaign names match this search." : "No campaigns in this state."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Panel>

        <div style={{ height: 16 }} />

        <Panel
          full
          title="Published campaign history"
          desc="Campaign definitions made available for authorized communications processing"
        >
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Publication history table">
            <table className="ae-table">
              <caption className="sr-only">
                Published campaign definitions and their operational handoff status.
              </caption>
              <thead>
                <tr>
                  <th scope="col">Campaign</th>
                  <th scope="col">Program</th>
                  <th scope="col">Evidence period</th>
                  <th scope="col" style={{ textAlign: "right" }}>
                    Audience (covered people)
                  </th>
                  <th scope="col">Publication</th>
                  <th scope="col">Published date</th>
                </tr>
              </thead>
              <tbody>
                {(history || []).map((record, i) => {
                  const campaign = campaignById.get(record.campaignId);
                  const programDomain = record.programDomain || campaign?.programDomain;
                  return (
                    <tr key={record.campaignId + i}>
                      <td>{record.campaignName || campaign?.name || "Campaign name unavailable"}</td>
                      <td>{DOMAIN_LABELS[programDomain] || record.programName || "Unavailable"}</td>
                      <td>{record.runMonth || "Unavailable"}</td>
                      <td style={{ textAlign: "right", fontWeight: 700 }}>
                        {formatCount(record.targetCohortCount)}
                      </td>
                      <td>
                        <StatusPill {...publicationStatus(record.workdayPayloadStatus)} />
                      </td>
                      <td style={{ color: "var(--bi-muted)", fontSize: 12 }}>
                        <CampaignDate value={record.deployedAt} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      </>
    );
  };

  return (
    <Shell
      tenant={tenant}
      auth={auth}
      activeNav="campaigns"
      title="Campaigns"
      subtitle="Create, review, approve, and publish aggregate campaigns"
      headerRight={
        <Link className="bi-btn-primary" to={campaignBuilderRoute({ source: "campaigns" })}>
          <Icon name="plus" size={14} /> Create campaign
        </Link>
      }
    >
      {body()}
    </Shell>
  );
}
